import { UserAccount } from 'shared/types/userAccount';
import { WorkerTask } from 'shared/types/task';
import { ExpertJobMetric, isCampaignTaskPayoutMetric } from 'domains/jobs/model/expertJobMetric';

/**
 * The source assigned-jobs view does not render every active EJM directly. It
 * first resolves the work that this expert can actually continue or claim.
 * Keep that summary separate from the Firestore service so the eligibility
 * rules can be tested without touching data.
 */
export interface ActiveEjmSummary {
  ejm: ExpertJobMetric;
  numUniqueEligibleClientTaskIds: number;
  /** Assigned work needing review plus eligible, unassigned work. */
  eligibleTasks: WorkerTask[];
}

export interface ActiveJobAssignmentProjection {
  eligibleEjmSummaries: ActiveEjmSummary[];
  ineligibleExpertJobMetrics: ExpertJobMetric[];
  taskCountsByJobId: Record<string, JobTaskCounts>;
  /** EJM IDs that aligned persists as no longer eligible during this read. */
  metricsToMarkIneligible: string[];
  eligibilityUpdatedAt: string;
}

export interface JobTaskCounts {
  completed: number;
  inProgress: number;
  pending: number;
  total: number;
}

export interface HistoricalJobLists {
  completed: ExpertJobMetric[];
  missed: ExpertJobMetric[];
}

const NEEDS_REVIEW_STATUSES = new Set(['unreviewed', 'inProgress']);

function needsReview(task: WorkerTask): boolean {
  return NEEDS_REVIEW_STATUSES.has(task.taskStatus);
}

function groupTasksByJob(tasks: WorkerTask[]): Map<string, WorkerTask[]> {
  const grouped = new Map<string, WorkerTask[]>();
  for (const task of tasks) {
    const current = grouped.get(task.jobId) ?? [];
    current.push(task);
    grouped.set(task.jobId, current);
  }
  return grouped;
}

function hasMatchingExpertPool(task: WorkerTask, expert: UserAccount): boolean {
  return (
    task.expertPoolIds.length === 0 ||
    task.expertPoolIds.some((poolId) => expert.assignedExpertPoolIds.includes(poolId))
  );
}

function timestampValue(value: string | null): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Mirrors aligned's `verifyEjmEligibileForTaskAssignment` exactly, including
 * its intentional preservation of an assignment when `tasksAssigned > 0`.
 * In that state the expert must still be able to resume already-assigned work
 * even if no new unassigned task is currently visible.
 */
export function evaluateEjmEligibility(
  ejm: ExpertJobMetric,
  eligibleTasks: WorkerTask[],
  now: Date,
): { eligible: boolean; shouldPersistIneligible: boolean } {
  if (!ejm.isEligibleForTaskAssignment) {
    return { eligible: false, shouldPersistIneligible: false };
  }

  let eligible = true;
  const nowMs = now.getTime();
  const dueAtMs = timestampValue(ejm.jobDueAt);
  if (dueAtMs !== null && dueAtMs < nowMs) {
    eligible = false;
  }

  // This ordering matches aligned: an expert with assigned work remains in
  // the active list so the task container can resume it.
  if (ejm.tasksAssigned > 0) {
    return { eligible: true, shouldPersistIneligible: false };
  }

  if (eligibleTasks.length === 0) {
    eligible = false;
  } else if (ejm.maxTaskLimit !== null && ejm.tasksCompleted >= ejm.maxTaskLimit) {
    eligible = false;
  }

  return { eligible, shouldPersistIneligible: !eligible };
}

/**
 * Builds the active/completed-source boundary used by the aligned assigned
 * jobs view. It intentionally keeps ineligible metrics separate: the view
 * model later folds them into Completed or Missed, just like aligned.
 */
export function buildActiveJobAssignmentProjection(params: {
  expert: UserAccount;
  activeMetrics: ExpertJobMetric[];
  assignedTasks: WorkerTask[];
  unassignedTasks: WorkerTask[];
  now?: Date;
}): ActiveJobAssignmentProjection {
  const now = params.now ?? new Date();
  const eligibilityUpdatedAt = now.toISOString();
  const activeMetrics = params.activeMetrics.filter((metric) => !isCampaignTaskPayoutMetric(metric));

  // The aligned service fails closed if an EJM does not belong to the expert
  // being loaded. The Firestore query should already enforce this, but keeping
  // the guard here protects the browser boundary from malformed fixtures/data.
  if (activeMetrics.some((metric) => metric.expertId !== params.expert.id)) {
    return {
      eligibleEjmSummaries: [],
      ineligibleExpertJobMetrics: [],
      taskCountsByJobId: {},
      metricsToMarkIneligible: [],
      eligibilityUpdatedAt,
    };
  }

  const assignedByJob = groupTasksByJob(params.assignedTasks);
  const unassignedByJob = groupTasksByJob(params.unassignedTasks);
  const eligibleEjmSummaries: ActiveEjmSummary[] = [];
  const ineligibleExpertJobMetrics: ExpertJobMetric[] = [];
  const metricsToMarkIneligible: string[] = [];

  for (const ejm of activeMetrics) {
    if (!ejm.isEligibleForTaskAssignment) {
      ineligibleExpertJobMetrics.push(ejm);
      continue;
    }

    const assignedTasks = assignedByJob.get(ejm.jobId) ?? [];
    const excludedClientTaskIds = new Set(assignedTasks.map((task) => task.clientTaskId));
    const eligibleAssignedTasks = assignedTasks.filter(needsReview);
    const eligibleUnassignedTasks = (unassignedByJob.get(ejm.jobId) ?? []).filter(
      (task) => !excludedClientTaskIds.has(task.clientTaskId) && hasMatchingExpertPool(task, params.expert),
    );
    const eligibleTasks = [...eligibleAssignedTasks, ...eligibleUnassignedTasks];
    const eligibility = evaluateEjmEligibility(ejm, eligibleTasks, now);

    if (eligibility.eligible) {
      eligibleEjmSummaries.push({
        ejm,
        numUniqueEligibleClientTaskIds: new Set(eligibleTasks.map((task) => task.clientTaskId)).size,
        eligibleTasks,
      });
    } else {
      ineligibleExpertJobMetrics.push(ejm);
      if (eligibility.shouldPersistIneligible) {
        metricsToMarkIneligible.push(ejm.id);
      }
    }
  }

  return {
    eligibleEjmSummaries,
    ineligibleExpertJobMetrics,
    taskCountsByJobId: {},
    metricsToMarkIneligible,
    eligibilityUpdatedAt,
  };
}

/** Mirrors aligned's getRemainingTasksForEjmSummary. */
export function getRemainingTasksForEjmSummary(summary: ActiveEjmSummary): number {
  const { ejm, numUniqueEligibleClientTaskIds } = summary;
  if (ejm.maxTaskLimit === null) {
    return Math.max(ejm.tasksAssigned, numUniqueEligibleClientTaskIds, 0);
  }

  return Math.max(
    ejm.tasksAssigned,
    Math.min(ejm.maxTaskLimit - ejm.tasksCompleted, numUniqueEligibleClientTaskIds),
    0,
  );
}

/** Mirrors aligned's `getTaskCountsForJobs` status buckets. */
export function countJobTasks(tasks: WorkerTask[]): Record<string, JobTaskCounts> {
  const counts: Record<string, JobTaskCounts> = {};
  for (const task of tasks) {
    // Aligned's getTasksByJobId excludes auto-rated tasks before counting.
    if (task.isAutoRated === true) continue;

    const current = counts[task.jobId] ?? { completed: 0, inProgress: 0, pending: 0, total: 0 };
    current.total += 1;
    if (task.taskStatus === 'approved' || task.taskStatus === 'approvedAdjusted' || task.taskStatus === 'underReview') {
      current.completed += 1;
    } else if (task.taskStatus === 'inProgress') {
      current.inProgress += 1;
    } else if (task.taskStatus === 'unreviewed') {
      current.pending += 1;
    }
    counts[task.jobId] = current;
  }
  return counts;
}

/**
 * Past-due rows are processed before active-but-ineligible rows. That order is
 * observable in aligned when the same EJM appears in both reads, so preserve
 * it while removing duplicate IDs.
 */
export function splitHistoricalJobMetrics(
  pastDueMetrics: ExpertJobMetric[],
  ineligibleMetrics: ExpertJobMetric[],
): HistoricalJobLists {
  const completed: ExpertJobMetric[] = [];
  const missed: ExpertJobMetric[] = [];
  const processedIds = new Set<string>();

  for (const metric of [...pastDueMetrics, ...ineligibleMetrics]) {
    if (isCampaignTaskPayoutMetric(metric)) continue;
    if (processedIds.has(metric.id)) continue;
    processedIds.add(metric.id);
    if (metric.tasksCompleted > 0) {
      completed.push(metric);
    } else {
      missed.push(metric);
    }
  }

  return { completed, missed };
}

export function sortActiveJobSummaries(summaries: ActiveEjmSummary[]): ActiveEjmSummary[] {
  return [...summaries].sort((a, b) => compareNullableAscending(a.ejm.jobDueAt, b.ejm.jobDueAt));
}

export function sortHistoricalJobMetrics(metrics: ExpertJobMetric[]): ExpertJobMetric[] {
  return [...metrics].sort((a, b) =>
    compareNullableDescending(a.lastUpdatedAt ?? a.jobDueAt, b.lastUpdatedAt ?? b.jobDueAt),
  );
}

function compareNullableAscending(a: string | null, b: string | null): number {
  const aMs = timestampValue(a);
  const bMs = timestampValue(b);
  if (aMs === null && bMs === null) return 0;
  if (aMs === null) return 1;
  if (bMs === null) return -1;
  return aMs - bMs;
}

function compareNullableDescending(a: string | null, b: string | null): number {
  const aMs = timestampValue(a);
  const bMs = timestampValue(b);
  if (aMs === null && bMs === null) return 0;
  if (aMs === null) return 1;
  if (bMs === null) return -1;
  return bMs - aMs;
}
